import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective } from 'ngx-mask';
import { CustomerService } from '../services/customers.service';
import { finalize } from 'rxjs';
import {
  customerNameValidator,
  customerEmailValidator,
  customerRemarksValidator,
  optional,
} from '../custom-validator/customer.validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GetCountriesService } from '@app/pages/home-page/services/get-countries.service';
import { Country, CountriesList } from '@app/utils/interfaces/countries-list';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { COUNTRY_NAME_TO_CODE_MAP } from '@app/utils/constants/country-mapping.constants';
import { CountryFormatService } from '@app/pages/home-page/user-profile/services/country-format.service';
import { CreateCustomerPayload, CustomerFormGroup } from '@app/utils/interfaces/customer.interface';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { MonitoringService } from '@app/services/monitoring/monitoring.service';

@Component({
  selector: 'app-create-customer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    NgxMaskDirective,
    IconComponent,
  ],

  templateUrl: './create-customer-dialog.component.html',
  styleUrls: ['./create-customer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCustomerDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<CreateCustomerDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly customerService = inject(CustomerService);
  private readonly countryService = inject(GetCountriesService);
  private readonly countryFormatService = inject(CountryFormatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly monitoring = inject(MonitoringService);

  protected customerForm!: FormGroup<CustomerFormGroup>;
  protected countries: Country[] = [];
  private phoneReg: RegExp | null = null;
  protected phoneMask = '';
  protected phonePrefix = signal('');
  protected isPhoneFocused = signal(false);

  protected createCustomerLoading = signal(false);
  protected getCountriesLoading = signal(false);

  public readonly iconName = IconName;
  public readonly countryNameToCodeMap = COUNTRY_NAME_TO_CODE_MAP;

  public ngOnInit(): void {
    this.getCountries();

    this.customerForm = this.fb.group<CustomerFormGroup>({
      firstName: this.fb.nonNullable.control('', [customerNameValidator()]),
      lastName: this.fb.nonNullable.control('', [customerNameValidator()]),
      email: this.fb.nonNullable.control('', [customerEmailValidator()]),
      remarks: this.fb.control(null, [customerRemarksValidator()]),
      countryId: this.fb.control(null),
      phoneNumber: this.fb.control(null),
    });

    this.customerForm.controls.countryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryId) => {
        if (countryId !== null) {
          this.setPhoneValidations(countryId);
        }
      });
  }

  protected get hasPhoneError(): boolean {
    const control = this.customerForm.controls.phoneNumber;
    return control.invalid && (control.dirty || control.touched);
  }

  protected onPhoneFocus(): void {
    this.isPhoneFocused.set(true);
  }

  protected onPhoneBlur(): void {
    this.isPhoneFocused.set(false);
  }

  protected getPhoneCode(): string {
    const countryId = this.customerForm.controls.countryId.value;
    const country = this.countries.find((c) => c.id === countryId);
    return country?.phoneCode ?? '';
  }

  protected getCountryCode(): string {
    const countryId = this.customerForm.controls.countryId.value;
    const country = this.countries.find((c) => c.id === countryId);
    return country ? this.countryNameToCodeMap[country.name] : '';
  }

  protected onSave(): void {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      return;
    }

    const payload = this.buildCustomerPayload();

    this.createCustomerLoading.set(true);
    this.customerService
      .createCustomer(payload)
      .pipe(
        finalize(() => {
          this.createCustomerLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.monitoring.trackEvent('customer_created');
          this.dialogRef.close('success');
        },
        error: () => {
          this.handleOpenErrorModal('customers.apiErrors.createCustomer');
        },
      });
  }

  public onCancel(): void {
    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    this.dialogRef.addPanelClass('hidden-dialog');

    confirmRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close();
        } else {
          this.dialogRef.removePanelClass('hidden-dialog');
        }
      });
  }

  private buildCustomerPayload(): CreateCustomerPayload {
    const formValue = this.customerForm.getRawValue();
    const country = this.countries.find((c) => c.id === formValue.countryId);

    const payload: CreateCustomerPayload = {
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      email: formValue.email ?? '',
    };

    if (formValue.countryId) {
      payload.countryId = formValue.countryId;
    }

    if (formValue.phoneNumber && country) {
      const formattedPhone = this.countryFormatService.formatPhoneForSubmit(
        formValue.phoneNumber,
        country.name,
        country.phoneMask,
      );
      payload.phoneNumber = formattedPhone ?? formValue.phoneNumber;
    }

    if (formValue.remarks) {
      payload.remarks = formValue.remarks;
    }

    return payload;
  }

  private getCountries(): void {
    this.getCountriesLoading.set(true);
    this.countryService
      .getCountries()
      .pipe(
        finalize(() => {
          this.getCountriesLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ data }: CountriesList) => {
          this.countries = data ?? [];
          const firstCountry = data?.[0];

          if (firstCountry) {
            this.setPhoneValidations(firstCountry.id);
            this.updatePhoneControls(true, firstCountry.id);
            return;
          }

          this.updatePhoneControls(false);
        },
        error: () => {
          this.updatePhoneControls(false);
          this.handleOpenErrorModal('customers.apiErrors.getCountries');
        },
      });
  }

  private setPhoneValidations(countryId: number): void {
    const country = this.countries.find((c) => c.id === countryId);
    if (!country) return;

    this.phoneReg = new RegExp(country.phoneMask);

    const phoneControl = this.customerForm.controls.phoneNumber;
    if (!phoneControl) return;

    const config = this.countryFormatService.configurePhoneControl(
      phoneControl,
      country.name,
      this.phoneReg,
    );

    const existingValidator = phoneControl.validator;

    phoneControl.clearValidators();
    phoneControl.setValidators(existingValidator ? [optional(existingValidator)] : []);

    this.phoneMask = config.mask;
    this.phonePrefix.set(config.prefix);

    phoneControl.updateValueAndValidity({ emitEvent: false });
  }

  private updatePhoneControls(enabled: boolean, countryId?: number): void {
    const countryIdCtrl = this.customerForm.controls.countryId;
    const phoneNumberCtrl = this.customerForm.controls.phoneNumber;

    if (!enabled) {
      countryIdCtrl.disable({ emitEvent: false });
      phoneNumberCtrl.disable({ emitEvent: false });
      return;
    }

    countryIdCtrl.enable({ emitEvent: false });
    phoneNumberCtrl.enable({ emitEvent: false });

    if (countryId) countryIdCtrl.setValue(countryId, { emitEvent: false });
  }

  private handleOpenErrorModal(message = 'customers.table.errors.customersLoadFail'): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        success: false,
        title: 'customers.table.errors.title',
        description: message,
      },
    });
  }
}
